import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "next-i18next/pages";
import HeroChat from "./HeroChat";
import { OPEN_CHAT_EVENT } from "../../lib/chatEvents";
import styles from "../../styles/home/Header.module.css";

interface HeaderProps {}

const TYPE_START_DELAY = 900;
const TYPE_SPEED = 105;
const DELETE_SPEED = 50;
const LINE_PAUSE = 600;
const PHRASE_HOLD = 3800;
const PHRASE_PAUSE = 650;

/* Types phrase 1 line by line, holds it, erases it, then phrase 2 — looping.
   Server-side (and with reduced motion / without JS) phrase 1 stays fully visible. */
const useTypedTitle = (phrases: [string, string][]) => {
  const [state, setState] = useState({ phrase: 0, count: phrases[0].join("").length });
  const [animating, setAnimating] = useState(false);
  const key = JSON.stringify(phrases);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const parsed: [string, string][] = JSON.parse(key);
    let phrase = 0;
    let count = 0;
    let deleting = false;
    let timer: number;

    const step = () => {
      const [lead, accent] = parsed[phrase];
      const total = lead.length + accent.length;
      let delay: number;

      if (deleting) {
        count -= 1;
        if (count <= 0) {
          deleting = false;
          phrase = (phrase + 1) % parsed.length;
          delay = PHRASE_PAUSE;
        } else {
          delay = DELETE_SPEED;
        }
      } else {
        count += 1;
        if (count >= total) {
          deleting = true;
          delay = PHRASE_HOLD;
        } else {
          delay = count === lead.length ? LINE_PAUSE : TYPE_SPEED;
        }
      }

      setState({ phrase, count });
      timer = window.setTimeout(step, delay);
    };

    setAnimating(true);
    setState({ phrase: 0, count: 0 });
    timer = window.setTimeout(step, TYPE_START_DELAY);

    return () => window.clearTimeout(timer);
  }, [key]);

  const [lead, accent] = phrases[state.phrase];
  return {
    lead,
    accent,
    leadTyped: lead.slice(0, Math.min(state.count, lead.length)),
    accentTyped: accent.slice(0, Math.max(0, state.count - lead.length)),
    caretOnLead: state.count <= lead.length,
    animating
  };
};

const Header: FunctionComponent<HeaderProps> = () => {
  const { t } = useTranslation("common");
  const [chatActive, setChatActive] = useState<boolean>(false);

  const phrases: [string, string][] = [
    [t("homeModern.hero.titleLead"), t("homeModern.hero.titleAccent")],
    [t("homeModern.hero.titleLead2"), t("homeModern.hero.titleAccent2")]
  ];
  const title = useTypedTitle(phrases);
  const phrasesKey = JSON.stringify(phrases);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  /* Shrinks the heading (via --title-fit) until the widest line of either
     phrase fits without wrapping, so the size stays constant while the
     typing animation alternates. */
  useEffect(() => {
    const heading = headingRef.current;
    const measure = measureRef.current;
    if (!heading || !measure) return undefined;

    let lastWidth = -1;

    const fit = () => {
      heading.style.removeProperty("--title-fit");
      lastWidth = heading.clientWidth;
      const scale = Math.min(1, heading.clientWidth / (measure.scrollWidth + 8));
      heading.style.setProperty("--title-fit", scale.toFixed(4));
    };

    fit();
    document.fonts?.ready.then(fit);
    window.addEventListener("resize", fit);
    /* Re-fit when the container width changes without a window resize
       (breakpoint-driven layout shifts, scrollbar toggles). The width guard
       keeps the font-size writes from re-triggering the observer. */
    const observer = new ResizeObserver(() => {
      if (heading.clientWidth !== lastWidth) fit();
    });
    observer.observe(heading);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [phrasesKey]);

  const collapsed = chatActive ? ` ${styles.copyCollapsed}` : "";

  return (
    <header id="home" className={styles.headerContainer}>
      <div className={styles.visual} aria-hidden="true">
        {/* Pre-generated webp variants (see public/images/home/header/);
            srcSet lets the browser pick per viewport width × DPR, which
            next/image's fill layout can't express with static files. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          className={styles.visualImage}
          src="/images/home/header/hero-cover-1672.webp"
          srcSet="/images/home/header/hero-cover-640.webp 640w,
            /images/home/header/hero-cover-960.webp 960w,
            /images/home/header/hero-cover-1280.webp 1280w,
            /images/home/header/hero-cover-1672.webp 1672w"
          sizes="100vw"
          loading="eager"
          fetchPriority="high"
          alt=""
        />
      </div>

      <svg
        className={styles.logoEdges}
        viewBox="0 0 1200 760"
        preserveAspectRatio="none"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="hero-logo-edge"
            x1="88"
            y1="626"
            x2="1134"
            y2="126"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFFFFF" />
            <stop offset=".42" stopColor="#FF8D79" />
            <stop offset="1" stopColor="#6CBFFF" />
          </linearGradient>
          <filter id="hero-logo-edge-blur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="26" />
          </filter>
        </defs>

        <g
          className={`${styles.logoEdge} ${styles.logoEdgeDesktop} ${styles.logoEdgeLower}`}
          strokeLinecap="square"
          strokeLinejoin="miter"
        >
          <path
            className={styles.logoEdgeHalo}
            d="M30 226V646H286"
            pathLength="1000"
          />
          <path
            className={styles.logoEdgePath}
            d="M30 226V646H286"
            pathLength="1000"
          />
          <path
            className={styles.logoEdgeGlint}
            d="M30 226V646H286"
            pathLength="1000"
          />
        </g>

        <g
          className={`${styles.logoEdge} ${styles.logoEdgeDesktop} ${styles.logoEdgeUpper}`}
          strokeLinecap="square"
          strokeLinejoin="miter"
        >
          <path
            className={styles.logoEdgeHalo}
            d="M760 68H1174V568"
            pathLength="1000"
          />
          <path
            className={styles.logoEdgePath}
            d="M760 68H1174V568"
            pathLength="1000"
          />
          <path
            className={styles.logoEdgeGlint}
            d="M760 68H1174V568"
            pathLength="1000"
          />
        </g>

        <g
          className={`${styles.logoEdge} ${styles.logoEdgeMobile} ${styles.logoEdgeLowerMobile}`}
          strokeLinecap="square"
          strokeLinejoin="miter"
        >
          <path
            className={styles.logoEdgeHalo}
            d="M32 382V720H300"
            pathLength="1000"
          />
          <path
            className={styles.logoEdgePath}
            d="M32 382V720H300"
            pathLength="1000"
          />
          <path
            className={styles.logoEdgeGlint}
            d="M32 382V720H300"
            pathLength="1000"
          />
        </g>

        <g
          className={`${styles.logoEdge} ${styles.logoEdgeMobile} ${styles.logoEdgeUpperMobile}`}
          strokeLinecap="square"
          strokeLinejoin="miter"
        >
          <path
            className={styles.logoEdgeHalo}
            d="M640 118H1168V650"
            pathLength="1000"
          />
          <path
            className={styles.logoEdgePath}
            d="M640 118H1168V650"
            pathLength="1000"
          />
          <path
            className={styles.logoEdgeGlint}
            d="M640 118H1168V650"
            pathLength="1000"
          />
        </g>
      </svg>

      <div className={styles.content}>
        <div className={`${styles.heroCopy}${collapsed}`}>
          <div className={styles.eyebrow}>{t("homeModern.hero.eyebrow")}</div>

          <h1
            ref={headingRef}
            className={styles.primaryHeading}
            aria-label={`${t("homeModern.hero.titleLead")} ${t("homeModern.hero.titleAccent")}`}
          >
            <span className={styles.titleLead} aria-hidden="true">
              <span className={styles.typedGhost}>{title.lead}</span>
              <span className={styles.typedOverlay}>
                {title.leadTyped}
                {title.animating && title.caretOnLead && <span className={styles.caret} />}
              </span>
            </span>
            <strong className={styles.titleAccent} aria-hidden="true">
              <span className={styles.typedGhost}>{title.accent}</span>
              <span className={styles.typedOverlay}>
                {title.accentTyped}
                {title.animating && !title.caretOnLead && <span className={styles.caret} />}
              </span>
            </strong>
            <span ref={measureRef} className={styles.titleMeasure} aria-hidden="true">
              {phrases.flat().map((line) => (
                <span key={line}>{line}</span>
              ))}
            </span>
          </h1>
        </div>

        <div className={`${styles.heroSide}${collapsed}`}>
          <p className={styles.subHeading}>{t("homeModern.hero.description")}</p>

          <div className={styles.ctaRow}>
            <button
              type="button"
              className={styles.primaryButton}
              data-umami-event="hero-chat-cta"
              onClick={() => window.dispatchEvent(new Event(OPEN_CHAT_EVENT))}
            >
              {t("homeModern.hero.primaryCta")} <span aria-hidden="true">↓</span>
            </button>

            <a href="#solutions" className={styles.secondaryButton}>
              {t("homeModern.hero.secondaryCta")}
            </a>
          </div>
        </div>

        <HeroChat onChatModeChange={setChatActive} />
      </div>

      {!chatActive && (
        <a
          className={styles.scrollButton}
          href="#pain-points"
          aria-label={t("homeModern.hero.scrollLabel")}
        >
          {t("homeModern.hero.scrollLabel")}
        </a>
      )}
    </header>
  );
};

export default Header;
